import type { AmlWatchlistEntry, InsightWatchlist, WatchlistHit } from '@onefootprint/request-types/dashboard';
import type { FormattedWatchlist, FormattedWatchlistEntity } from '../../../onboarding-business-insight.types';
import { EMPTY_VALUE } from '../constants';

type RegistryEntity = Extract<FormattedWatchlistEntity, { source: 'registry' }>;

export const formatWatchlist = (
  watchlist: InsightWatchlist | undefined,
  aml: AmlWatchlistEntry[],
): FormattedWatchlist => {
  const formatHit = (hit: WatchlistHit) => {
    const {
      agency,
      agencyAbbr,
      agencyInformationUrl,
      agencyListUrl,
      entityAliases,
      entityName,
      listCountry,
      listName,
      url,
    } = hit;
    return {
      agency,
      agencyAbbr: agencyAbbr ?? EMPTY_VALUE,
      agencyInformationUrl: agencyInformationUrl ?? EMPTY_VALUE,
      agencyListUrl: agencyListUrl,
      entityAliases: entityAliases.filter(alias => Boolean(alias)),
      entityName: entityName ?? EMPTY_VALUE,
      listCountry,
      listName: listName ?? EMPTY_VALUE,
      url: url ?? EMPTY_VALUE,
    };
  };

  // ComplyAdvantage screening replaces the KYB vendor's lists rather than adding to them: the two
  // model a hit differently, so one selector holding both would compare unlike things.
  const amlEntries = aml.filter(({ hits }) => hits.length > 0);
  if (amlEntries.length > 0) {
    return {
      hitCount: amlEntries.reduce((total, { hits }) => total + hits.length, 0),
      watchlist: Object.fromEntries(
        amlEntries.map(({ screenedEntityName, shareUrl, hits }) => {
          const entityName = screenedEntityName || EMPTY_VALUE;
          return [
            `${entityName}-aml`,
            { screenedEntityName: entityName, kind: 'business' as const, source: 'aml' as const, shareUrl, hits },
          ];
        }),
      ),
    };
  }

  if (!watchlist) {
    return { hitCount: 0, watchlist: {} };
  }

  const entities: Record<string, RegistryEntity> = {};
  watchlist.business.forEach(({ screenedEntityName, hits }) => {
    const entityName = screenedEntityName || EMPTY_VALUE;
    const existingHits = entities[`${entityName}-business`]?.hits || [];
    entities[`${entityName}-business`] = {
      screenedEntityName: entityName,
      kind: 'business',
      source: 'registry',
      hits: [...existingHits, ...hits.map(hit => formatHit(hit))],
    };
  });
  watchlist.people.forEach(({ screenedEntityName, hits }) => {
    const entityName = screenedEntityName || EMPTY_VALUE;
    const existingHits = entities[`${entityName}-person`]?.hits || [];
    entities[`${entityName}-person`] = {
      screenedEntityName: entityName,
      kind: 'person',
      source: 'registry',
      hits: [...existingHits, ...hits.map(hit => formatHit(hit))],
    };
  });
  return { hitCount: watchlist.hitCount ?? 0, watchlist: entities };
};
